import { FlightTripStop, TripStop } from '../../../../../../types/trip.types';

export type FieldType = 'text' | 'date' | 'time' | 'dateTime' | 'address' | 'country' | 'airport';

export interface ActivityField {
  key: string;
  label: string;
  type: FieldType;
  getValue: (activity: TripStop) => string | null;
  sectionDividerBefore?: boolean;
  sectionDividerAfter?: boolean;
  fieldGroupId?: string;
}

export interface SmartToolsConfig {
  [activityType: string]: string[];
}

const DEFAULT_SMART_TOOLS = ['Go Now', 'Packing List'];

export const ACTIVITY_FIELDS_CONFIG: { [key: string]: ActivityField[] } = {
  Flight: [
    {
      key: 'from',
      label: 'From',
      type: 'airport',
      getValue: (activity: TripStop) => {
        if (activity.stop_type !== 'Flight') return null;
        const flight = activity as FlightTripStop;
        const label = flight.flight_from_airport_name;
        return label || null;
      },
    },
    {
      key: 'to',
      label: 'To',
      type: 'airport',
      getValue: (activity: TripStop) => {
        if (activity.stop_type !== 'Flight') return null;
        const flight = activity as FlightTripStop;
        const label = flight.flight_to_airport_name;
        return label || null;
      },
    },
    {
      key: 'flightNumber',
      label: 'Flight #',
      type: 'text',
      getValue: (activity: any) => activity.flight_number || null,
    },
    {
      key: 'airline',
      label: 'Airline',
      type: 'text',
      getValue: (activity: any) => activity.airline || null,
      fieldGroupId: 'flight-booking',
    },
    {
      key: 'booking',
      label: 'Ticket/Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
      fieldGroupId: 'flight-booking',
    },
    {
      key: 'seat',
      label: 'Seat #',
      type: 'text',
      getValue: (activity: any) => activity.seat || null,
      fieldGroupId: 'flight-booking',
    },
    {
      key: 'departure',
      label: 'Departure',
      type: 'dateTime',
      getValue: (activity: any) => activity.departure_date_time || null,
      fieldGroupId: 'flight-schedule',
    },
    {
      key: 'arrival',
      label: 'Arrival',
      type: 'dateTime',
      getValue: (activity: any) => activity.arrival_date_time || null,
      fieldGroupId: 'flight-schedule',
    },
  ],

  Hotel: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'checkIn',
      label: 'Check-in',
      type: 'dateTime',
      getValue: (activity: any) => activity.check_in_date_time || null,
      fieldGroupId: 'hotel-stay',
    },
    {
      key: 'checkOut',
      label: 'Check-out',
      type: 'dateTime',
      getValue: (activity: any) => activity.check_out_date_time || null,
      fieldGroupId: 'hotel-stay',
    },
  ],

  Activity: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'activity-block',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'activity-block',
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
      fieldGroupId: 'activity-block',
    },
  ],

  CarRental: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'carDescription',
      label: 'Vehicle',
      type: 'text',
      getValue: (activity: any) => activity.transport_name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'pickupDate',
      label: 'Pick-up',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'car-dates',
    },
    {
      key: 'dropoffDate',
      label: 'Drop-off',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'car-dates',
    },
    {
      key: 'address',
      label: 'Office Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
      fieldGroupId: 'car-locs',
    },
    {
      key: 'pickupLocation',
      label: 'Pick-up Location',
      type: 'address',
      getValue: (activity: any) => activity.pick_up_address?.name || null,
      fieldGroupId: 'car-locs',
    },
    {
      key: 'dropoffLocation',
      label: 'Drop-off Location',
      type: 'address',
      getValue: (activity: any) => activity.drop_off_address?.name || null,
      fieldGroupId: 'car-locs',
    },
  ],

  Concert: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'booking',
      label: 'Ticket/Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'seat',
      label: 'Seat #',
      type: 'text',
      getValue: (activity: any) => activity.seat || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'concert-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'concert-dates',
    },
  ],

  Theatre: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Ticket/Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'seat',
      label: 'Seat #',
      type: 'text',
      getValue: (activity: any) => activity.seat || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'theatre-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'theatre-dates',
    },
  ],

  Meeting: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'meeting-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'meeting-dates',
    },
  ],

  Shopping: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'shopping-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'shopping-dates',
    },
  ],

  Restaurant: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'restaurant-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'restaurant-dates',
    },
  ],

  Tour: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'tour-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'tour-dates',
    },
  ],

  Parking: [
    {
      key: 'place',
      label: 'Place',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'parkingSpace',
      label: 'Parking Space #',
      type: 'text',
      getValue: (activity: any) => activity.parking_space || null,
      fieldGroupId: 'parking-block',
    },
    {
      key: 'startDate',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'parking-block',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'parking-block',
    },
  ],

  Directions: [
    {
      key: 'place',
      label: 'Route Name',
      type: 'text',
      getValue: (activity: any) => activity.place || null,
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'date',
      label: 'Start',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'directions-dates',
    },
    {
      key: 'endDate',
      label: 'End',
      type: 'dateTime',
      getValue: (activity: any) => activity.end_date_time || null,
      fieldGroupId: 'directions-dates',
    },
  ],

  BorderCrossing: [
    {
      key: 'from',
      label: 'From',
      type: 'country',
      getValue: (activity: any) => activity.country_from || null,
    },
    {
      key: 'to',
      label: 'To',
      type: 'country',
      getValue: (activity: any) => activity.country_to || null,
    },
    {
      key: 'borderCheckpoint',
      label: 'Border Checkpoint',
      type: 'text',
      getValue: (activity: any) => activity.border_checkpoint || null,
    },
    {
      key: 'date',
      label: 'Date',
      type: 'dateTime',
      getValue: (activity: any) => activity.start_date_time || null,
      fieldGroupId: 'border-meta',
    },
    {
      key: 'address',
      label: 'Address',
      type: 'address',
      getValue: (activity: any) => activity.address?.name || null,
      fieldGroupId: 'border-meta',
    },
  ],

  Ferry: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'carrier',
      label: 'Carrier Name',
      type: 'text',
      getValue: (activity: any) => activity.carrier_name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'seat',
      label: 'Seat #',
      type: 'text',
      getValue: (activity: any) => activity.seat || null,
    },
    {
      key: 'departure',
      label: 'Departure',
      type: 'dateTime',
      getValue: (activity: any) => activity.departure_date_time || null,
      fieldGroupId: 'ferry-times',
    },
    {
      key: 'arrival',
      label: 'Arrival',
      type: 'dateTime',
      getValue: (activity: any) => activity.arrival_date_time || null,
      fieldGroupId: 'ferry-times',
    },
    {
      key: 'departureLocation',
      label: 'Departure Location',
      type: 'text',
      getValue: (activity: any) => activity.location_from_name || null,
      fieldGroupId: 'ferry-dep',
    },
    {
      key: 'departureAddress',
      label: 'Departure Address',
      type: 'address',
      getValue: (activity: any) => activity.departure_address?.name || null,
      fieldGroupId: 'ferry-dep',
    },
    {
      key: 'vehicleDescription',
      label: 'Vehicle Description',
      type: 'text',
      getValue: (activity: any) => activity.vehicle_description || null,
      fieldGroupId: 'ferry-dep',
    },
    {
      key: 'arrivalLocation',
      label: 'Arrival Location',
      type: 'text',
      getValue: (activity: any) => activity.location_to_name || null,
      fieldGroupId: 'ferry-arr',
    },
    {
      key: 'arrivalAddress',
      label: 'Arrival Address',
      type: 'address',
      getValue: (activity: any) => activity.arrival_address?.name || null,
      fieldGroupId: 'ferry-arr',
    },
    {
      key: 'passengers',
      label: 'Number of Passengers',
      type: 'text',
      getValue: (activity: any) => activity.number_of_passengers?.toString() || null,
      fieldGroupId: 'ferry-arr',
    },
  ],

  Cruise: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'shipName',
      label: 'Ship Name',
      type: 'text',
      getValue: (activity: any) => activity.transport_name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'departure',
      label: 'Departure',
      type: 'dateTime',
      getValue: (activity: any) => activity.departure_date_time || null,
      fieldGroupId: 'cruise-times',
    },
    {
      key: 'arrival',
      label: 'Arrival',
      type: 'dateTime',
      getValue: (activity: any) => activity.arrival_date_time || null,
      fieldGroupId: 'cruise-times',
    },
    {
      key: 'departurePort',
      label: 'Departure Port',
      type: 'text',
      getValue: (activity: any) => activity.location_from_name || null,
      fieldGroupId: 'cruise-dep',
    },
    {
      key: 'departureAddress',
      label: 'Departure Port Address',
      type: 'address',
      getValue: (activity: any) => activity.starting_port_address?.name || null,
      fieldGroupId: 'cruise-dep',
    },
    {
      key: 'arrivalPort',
      label: 'Arrival Port',
      type: 'text',
      getValue: (activity: any) => activity.location_to_name || null,
      fieldGroupId: 'cruise-arr',
    },
    {
      key: 'arrivalAddress',
      label: 'Arrival Port Address',
      type: 'address',
      getValue: (activity: any) => activity.ending_port_address?.name || null,
      fieldGroupId: 'cruise-arr',
    },
    {
      key: 'cabinType',
      label: 'Cabin Type',
      type: 'text',
      getValue: (activity: any) => activity.cabin_type || null,
      fieldGroupId: 'cruise-cabin',
    },
    {
      key: 'cabin',
      label: 'Cabin #',
      type: 'text',
      getValue: (activity: any) => activity.cabin || null,
      fieldGroupId: 'cruise-cabin',
    },
  ],

  Rail: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'departure',
      label: 'Departure',
      type: 'dateTime',
      getValue: (activity: any) => activity.departure_date_time || null,
      fieldGroupId: 'rail-times',
    },
    {
      key: 'arrival',
      label: 'Arrival',
      type: 'dateTime',
      getValue: (activity: any) => activity.arrival_date_time || null,
      fieldGroupId: 'rail-times',
    },
    {
      key: 'departureStation',
      label: 'Departure Station',
      type: 'text',
      getValue: (activity: any) => activity.location_from_name || null,
      fieldGroupId: 'rail-dep',
    },
    {
      key: 'departureAddress',
      label: 'Departure Address',
      type: 'address',
      getValue: (activity: any) => activity.departure_address?.name || null,
      fieldGroupId: 'rail-dep',
    },
    {
      key: 'arrivalStation',
      label: 'Arrival Station',
      type: 'text',
      getValue: (activity: any) => activity.location_to_name || null,
      fieldGroupId: 'rail-arr',
    },
    {
      key: 'arrivalAddress',
      label: 'Arrival Address',
      type: 'address',
      getValue: (activity: any) => activity.arrival_address?.name || null,
      fieldGroupId: 'rail-arr',
    },
    {
      key: 'trainType',
      label: 'Train Type',
      type: 'text',
      getValue: (activity: any) => activity.train_type || null,
      fieldGroupId: 'rail-train',
    },
    {
      key: 'train',
      label: 'Train #',
      type: 'text',
      getValue: (activity: any) => activity.train || null,
      fieldGroupId: 'rail-train',
    },
    {
      key: 'class',
      label: 'Class',
      type: 'text',
      getValue: (activity: any) => activity.seat_type || null,
      fieldGroupId: 'rail-train',
    },
    {
      key: 'seat',
      label: 'Seat #',
      type: 'text',
      getValue: (activity: any) => activity.seat || null,
      fieldGroupId: 'rail-train',
    },
  ],

  Transportation: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      getValue: (activity: any) => activity.name || null,
    },
    {
      key: 'carrier',
      label: 'Carrier Name',
      type: 'text',
      getValue: (activity: any) => activity.carrier_name || null,
    },
    {
      key: 'departure',
      label: 'Departure',
      type: 'dateTime',
      getValue: (activity: any) => activity.departure_date_time || null,
      fieldGroupId: 'trans-times',
    },
    {
      key: 'arrival',
      label: 'Arrival',
      type: 'dateTime',
      getValue: (activity: any) => activity.arrival_date_time || null,
      fieldGroupId: 'trans-times',
    },
    {
      key: 'departureLocation',
      label: 'Departure Location',
      type: 'text',
      getValue: (activity: any) => activity.location_from_name || null,
      fieldGroupId: 'trans-dep',
    },
    {
      key: 'departureAddress',
      label: 'Departure Address',
      type: 'address',
      getValue: (activity: any) => activity.departure_address?.name || null,
      fieldGroupId: 'trans-dep',
    },
    {
      key: 'arrivalLocation',
      label: 'Arrival Location',
      type: 'text',
      getValue: (activity: any) => activity.location_to_name || null,
      fieldGroupId: 'trans-arr',
    },
    {
      key: 'arrivalAddress',
      label: 'Arrival Address',
      type: 'address',
      getValue: (activity: any) => activity.arrival_address?.name || null,
      fieldGroupId: 'trans-arr',
    },
    {
      key: 'booking',
      label: 'Booking #',
      type: 'text',
      getValue: (activity: any) => activity.booking || null,
    },
    {
      key: 'passengers',
      label: 'Number of Passengers',
      type: 'text',
      getValue: (activity: any) => activity.number_of_passengers?.toString() || null,
    },
    {
      key: 'vehicleType',
      label: 'Vehicle Type',
      type: 'text',
      getValue: (activity: any) => activity.vehicle_type || null,
    },
  ],
};

export const SMART_TOOLS_CONFIG: SmartToolsConfig = {
  Flight: ['Go Now', 'Alternative Flights', 'Packing List'],
  Hotel: [...DEFAULT_SMART_TOOLS],
  Activity: [...DEFAULT_SMART_TOOLS],
  CarRental: [...DEFAULT_SMART_TOOLS],
  Concert: [...DEFAULT_SMART_TOOLS],
  Theatre: [...DEFAULT_SMART_TOOLS],
  Meeting: [...DEFAULT_SMART_TOOLS],
  Shopping: [...DEFAULT_SMART_TOOLS],
  Restaurant: [...DEFAULT_SMART_TOOLS],
  Tour: [...DEFAULT_SMART_TOOLS],
  Parking: [...DEFAULT_SMART_TOOLS],
  Directions: [...DEFAULT_SMART_TOOLS],
  BorderCrossing: [...DEFAULT_SMART_TOOLS],
  Ferry: [...DEFAULT_SMART_TOOLS],
  Cruise: [...DEFAULT_SMART_TOOLS],
  Rail: [...DEFAULT_SMART_TOOLS],
  Transportation: [...DEFAULT_SMART_TOOLS],
};

export const getActivityFields = (activityType: string): ActivityField[] => {
  const fields = ACTIVITY_FIELDS_CONFIG[activityType];

  if (!fields) {
    console.warn(`No field configuration found for activity type: ${activityType}`);
    return [];
  }

  return fields;
};

export const getSmartTools = (activityType: string): string[] => {
  try {
    const tools = SMART_TOOLS_CONFIG[activityType];

    if (tools && Array.isArray(tools) && tools.length > 0) {
      return tools;
    }

    if (activityType && typeof activityType === 'string') {
      return [...DEFAULT_SMART_TOOLS];
    }

    return [...DEFAULT_SMART_TOOLS];
  } catch (error) {
    console.error(`Error getting smart tools for ${activityType}:`, error);
    return [...DEFAULT_SMART_TOOLS];
  }
};
